/**
 * AllotmentScopePanel — Design Consultation only: what the allotment is intended to cover.
 *
 * A Design Consultation agreement otherwise names only the Project. When a client wants more
 * specificity, this list prints in the agreement (web view and PDF) right under the allotment
 * paragraph, introduced by "The initial allotment is intended to cover the following scope:".
 * With no allotment amount set, it prints under the Project name instead.
 *
 * Blank = nothing prints; the agreement reads exactly as before. Stored in Supabase on the
 * proposal's contract-terms row (database/285), keyed by the proposal id, so the panel only
 * appears once the proposal has been saved.
 */
import { useEffect, useState } from 'react'
import { F } from './CrmModal.jsx'
import { alertDialog } from '../Dialog.jsx'
import MarkdownField from '../MarkdownField.jsx'
import { getContractTerms, saveAllotmentScope } from '../../services/proposalContractTerms.js'

export default function AllotmentScopePanel({ proposalId, allotmentAmount }) {
  const [loading, setLoading] = useState(true)
  const [text, setText] = useState('')
  const [dirty, setDirty] = useState(false)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    let cancelled = false
    if (!proposalId) { setLoading(false); return }
    getContractTerms(proposalId).then(t => {
      if (cancelled) return
      setText(t.allotmentScope || '')
      setLoading(false)
    }).catch(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [proposalId])

  async function save() {
    setBusy(true)
    try {
      const t = await saveAllotmentScope(proposalId, text)
      setText(t.allotmentScope || ''); setDirty(false)
    } catch (e) {
      await alertDialog({ title: 'Could not save', message: e.message })
    } finally { setBusy(false) }
  }

  if (!proposalId) return null
  const hasAllotment = Number(allotmentAmount) > 0
  const lead = hasAllotment
    ? 'The initial allotment is intended to cover the following scope:'
    : 'The Project is intended to include the following scope:'

  return (
    <div style={{ ...S.wrap, ...(text.trim() ? S.wrapOn : null) }}>
      <div style={S.head}>Stipulated scope</div>
      {loading ? (
        <div style={S.muted}>Loading…</div>
      ) : (
        <>
          <div style={S.hint}>
            Optional. List what this engagement is meant to cover, one item per line starting with
            &ldquo;- &rdquo;. It prints in the agreement {hasAllotment ? 'right under the allotment amount' : 'under the Project name (set an allotment amount and it moves under the allotment instead)'}, after the line:
            <em> {lead}</em> Leave blank to print nothing.
          </div>
          <MarkdownField
            value={text}
            onChange={v => { setText(v); setDirty(true) }}
            placeholder={'- Review of existing conditions and zoning constraints\n- Two concept options for the kitchen and family room\n- Up to three design meetings'}
            minHeight={110}
          />
          {dirty && (
            <div style={S.dirtyBanner}>
              <strong>Not saved.</strong> Preview PDF and the client&apos;s document still show the
              previously saved scope. Save first.
            </div>
          )}
          <div style={{ marginTop: 10 }}>
            <button style={F.btnPrimary} onClick={save} disabled={busy || !dirty}>
              {busy ? 'Saving…' : dirty ? 'Save scope' : 'Saved'}
            </button>
          </div>
        </>
      )}
    </div>
  )
}

const S = {
  wrap: { border: '0.5px solid var(--aj-border-mid)', borderRadius: 8, padding: '12px 14px', marginBottom: 16, background: 'var(--aj-bg-subtle)' },
  wrapOn: { borderColor: 'rgba(29,158,117,0.45)', background: 'rgba(29,158,117,0.05)' },
  head: { fontFamily: '"Montserrat", sans-serif', fontSize: 10, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--aj-text-mid)', marginBottom: 8 },
  muted: { fontFamily: '"DM Sans", sans-serif', fontSize: 12, color: 'var(--aj-text-muted)', lineHeight: 1.5 },
  hint: { fontFamily: '"DM Sans", sans-serif', fontSize: 11.5, color: 'var(--aj-text-muted)', lineHeight: 1.45, marginBottom: 8 },
  dirtyBanner: { fontFamily: '"DM Sans", sans-serif', fontSize: 12, lineHeight: 1.45, color: '#8a4b00', background: 'rgba(230,145,0,0.14)', border: '0.5px solid rgba(230,145,0,0.5)', borderRadius: 4, padding: '7px 10px', marginTop: 10 },
}
